import { getSupabase } from 'shared/supabase/client';

export type AssignmentOption = {
  id: string;
  text: string;
};

export type AssignmentQuestion = {
  id: string;
  question: string;
  options: AssignmentOption[];
};

export type AssignmentSubmissionSummary = {
  id: string;
  status: 'pending' | 'submitted' | 'graded';
  score: number | null;
  feedback: string | null;
  submittedAt: string | null;
  attemptNumber?: number;
  answersRevealed?: boolean;
};

export type AssignmentForAttempt = {
  id: string;
  batchId: string;
  title: string;
  description: string | null;
  dueDate: string;
  maxScore: number;
  maxAttempts: number;
  questionCount: number;
  questions: AssignmentQuestion[];
  attemptCount: number;
  retakeGranted: boolean;
  answersRevealed: boolean;
  canTake: boolean;
  submission: AssignmentSubmissionSummary | null;
};

export type AssignmentAnswer = {
  questionId: string;
  optionId: string;
};

export type AssignmentSubmitResult = {
  submissionId: string;
  totalQuestions: number;
  assignmentId: string;
  batchId: string;
  assignmentTitle: string;
  attemptNumber?: number;
  awaitingGuruGrade: boolean;
};

export type BatchAssignmentSummary = {
  id: string;
  batchId: string;
  title: string;
  description: string;
  dueDate: string;
  maxScore: number;
  questionCount: number;
  status: 'pending' | 'submitted' | 'graded';
  score?: number;
  feedback?: string | null;
  submittedAt?: string | null;
  attemptNumber?: number;
  answersRevealed?: boolean;
  retakeGranted?: boolean;
};

export type AssignmentReviewAnswer = {
  questionId: string;
  question: string;
  sortOrder: number;
  selectedOptionId: string | null;
  selectedOptionText: string | null;
  correctOptionId: string | null;
  correctOptionText: string | null;
  isCorrect: boolean;
  options: Array<{
    id: string;
    text: string;
    sortOrder: number;
    isCorrect: boolean;
    selected: boolean;
  }>;
};

export type AssignmentReview = {
  assignment: {
    id: string;
    batchId: string;
    title: string;
  };
  submission: {
    id: string;
    score: number | null;
    feedback: string | null;
    submittedAt: string | null;
    gradedAt: string | null;
    attemptNumber: number;
  };
  answers: AssignmentReviewAnswer[];
};

const ERROR_MAP: Record<string, string> = {
  not_authenticated: 'Please sign in to continue.',
  assignment_not_found: 'This assignment was not found or is no longer available.',
  not_enrolled: 'You must be actively enrolled in the batch to access this assignment.',
  no_questions: 'This assignment has no questions yet.',
  invalid_answers: 'Invalid answer payload.',
  past_due: 'The due date for this assignment has passed.',
  already_graded: 'This assignment has already been graded and cannot be resubmitted.',
  already_submitted: 'You already submitted this assignment.',
  not_published: 'This assignment is not available yet.',
  answers_revealed: 'Answers were revealed - this assignment is closed for new attempts.',
  max_attempts_reached: 'You have used all allowed attempts for this assignment.',
  retake_not_granted: 'Your guru has not granted another attempt yet.',
  not_revealed: 'Answer review is not available for this assignment.',
};

function mapRpcError(error: string | undefined, fallback: string): string {
  if (!error) return fallback;
  return ERROR_MAP[error] ?? error;
}

function mapQuestions(rows: Record<string, unknown>[]): AssignmentQuestion[] {
  return rows.map((q) => ({
    id: q.id as string,
    question: q.question as string,
    options: ((q.options as Record<string, unknown>[]) ?? []).map((o) => ({
      id: o.id as string,
      text: o.text as string,
    })),
  }));
}

export async function fetchAssignmentForAttempt(
  assignmentId: string
): Promise<AssignmentForAttempt> {
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc('stud_get_assignment_for_attempt', {
    p_assignment_id: assignmentId,
  });
  if (error) throw new Error(error.message);

  const payload = data as {
    ok: boolean;
    error?: string;
    assignment?: Record<string, unknown>;
  };
  if (!payload?.ok || !payload.assignment) {
    throw new Error(mapRpcError(payload?.error, 'Failed to load assignment'));
  }

  const assignment = payload.assignment;
  const submissionRaw = assignment.submission as Record<string, unknown> | null;

  return {
    id: assignment.id as string,
    batchId: assignment.batch_id as string,
    title: assignment.title as string,
    description: (assignment.description as string | null) ?? null,
    dueDate: assignment.due_date as string,
    maxScore: assignment.max_score as number,
    maxAttempts: (assignment.max_attempts as number | null) ?? 1,
    questionCount: assignment.question_count as number,
    questions: mapQuestions((assignment.questions as Record<string, unknown>[]) ?? []),
    attemptCount: (assignment.attempt_count as number | null) ?? 0,
    retakeGranted: Boolean(assignment.retake_granted),
    answersRevealed: Boolean(assignment.answers_revealed),
    canTake: Boolean(assignment.can_take),
    submission: submissionRaw
      ? {
          id: submissionRaw.id as string,
          status: submissionRaw.status as AssignmentSubmissionSummary['status'],
          score: (submissionRaw.score as number | null) ?? null,
          feedback: (submissionRaw.feedback as string | null) ?? null,
          submittedAt: (submissionRaw.submitted_at as string | null) ?? null,
          attemptNumber: (submissionRaw.attempt_number as number | null) ?? undefined,
          answersRevealed: Boolean(submissionRaw.answers_revealed),
        }
      : null,
  };
}

export async function submitAssignment(
  assignmentId: string,
  answers: AssignmentAnswer[]
): Promise<AssignmentSubmitResult> {
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc('stud_submit_assignment', {
    p_assignment_id: assignmentId,
    p_answers: answers.map((answer) => ({
      question_id: answer.questionId,
      option_id: answer.optionId,
    })),
  });
  if (error) throw new Error(error.message);

  const payload = data as {
    ok: boolean;
    error?: string;
    submission_id?: string;
    score?: number;
    total_questions?: number;
    assignment_id?: string;
    batch_id?: string;
    assignment_title?: string;
    attempt_number?: number;
    awaiting_guru_grade?: boolean;
  };
  if (!payload?.ok) {
    throw new Error(mapRpcError(payload?.error, 'Failed to submit assignment'));
  }

  return {
    submissionId: payload.submission_id as string,
    totalQuestions: payload.total_questions as number,
    assignmentId: payload.assignment_id as string,
    batchId: payload.batch_id as string,
    assignmentTitle: payload.assignment_title as string,
    attemptNumber: payload.attempt_number,
    awaitingGuruGrade: Boolean(payload.awaiting_guru_grade),
  };
}

export async function fetchMyBatchAssignments(batchId: string): Promise<BatchAssignmentSummary[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc('stud_list_batch_assignments', {
    p_batch_id: batchId,
  });
  if (error) throw new Error(error.message);

  const payload = data as {
    ok: boolean;
    error?: string;
    assignments?: Record<string, unknown>[];
  };
  if (!payload?.ok) {
    throw new Error(mapRpcError(payload?.error, 'Failed to load assignments'));
  }

  return (payload.assignments ?? []).map((row) => ({
    id: row.id as string,
    batchId: row.batch_id as string,
    title: row.title as string,
    description: (row.description as string) ?? '',
    dueDate: row.due_date as string,
    maxScore: row.max_score as number,
    questionCount: row.question_count as number,
    status: (row.status as BatchAssignmentSummary['status']) ?? 'pending',
    score: (row.score as number | undefined) ?? undefined,
    feedback: (row.feedback as string | null | undefined) ?? undefined,
    submittedAt: (row.submitted_at as string | null | undefined) ?? undefined,
    attemptNumber: (row.attempt_number as number | undefined) ?? undefined,
    answersRevealed: Boolean(row.answers_revealed),
    retakeGranted: Boolean(row.retake_granted),
  }));
}

export async function fetchAssignmentReview(assignmentId: string): Promise<AssignmentReview> {
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc('stud_get_assignment_review', {
    p_assignment_id: assignmentId,
  });
  if (error) throw new Error(error.message);

  const payload = data as {
    ok: boolean;
    error?: string;
    assignment?: Record<string, unknown>;
    submission?: Record<string, unknown>;
    answers?: Record<string, unknown>[];
  };
  if (!payload?.ok || !payload.assignment || !payload.submission) {
    throw new Error(mapRpcError(payload?.error, 'Failed to load answer review'));
  }

  return {
    assignment: {
      id: payload.assignment.id as string,
      batchId: payload.assignment.batch_id as string,
      title: payload.assignment.title as string,
    },
    submission: {
      id: payload.submission.id as string,
      score: (payload.submission.score as number | null) ?? null,
      feedback: (payload.submission.feedback as string | null) ?? null,
      submittedAt: (payload.submission.submitted_at as string | null) ?? null,
      gradedAt: (payload.submission.graded_at as string | null) ?? null,
      attemptNumber: (payload.submission.attempt_number as number) ?? 1,
    },
    answers: (payload.answers ?? []).map((row) => ({
      questionId: row.question_id as string,
      question: row.question as string,
      sortOrder: (row.sort_order as number) ?? 0,
      selectedOptionId: (row.selected_option_id as string | null) ?? null,
      selectedOptionText: (row.selected_option_text as string | null) ?? null,
      correctOptionId: (row.correct_option_id as string | null) ?? null,
      correctOptionText: (row.correct_option_text as string | null) ?? null,
      isCorrect: Boolean(row.is_correct),
      options: ((row.options as Record<string, unknown>[]) ?? []).map((o) => ({
        id: o.id as string,
        text: o.text as string,
        sortOrder: (o.sort_order as number) ?? 0,
        isCorrect: Boolean(o.is_correct),
        selected: Boolean(o.selected),
      })),
    })),
  };
}
